import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdf = "/img/pdf1.png";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const getEduLabel = (value) => {
  switch (value) {
    case "E0":
      return "All Education Categories";
    case "E1":
      return "Less than high school";
    case "E2":
      return "High school or equivalent, no college";
    case "E3":
      return "Some college or Associate degree";
    case "E4":
      return "Bachelor's degree or advanced degree";
    case "E5":
      return "Educational attainment not available (workers aged 24 or younger)";
    default:
      return "Unknown";
  }
};

const EducationTotalsStackedChart = (props) => {
  const pdfRef = useRef();
  const { data } = props;
  // Ensure data is valid and not empty
  if (!data || data.length === 0) {
    return <p>No data available</p>;
  }
  // Extract years dynamically from the first data object
  const years = Object.keys(data[0]).filter((key) => key.startsWith("total_"));

  // Prepare data for the stacked bar chart
  const chartData = {
    labels: years.map((year) => year.replace("total_", "")), // Format years for display
    datasets: data.map((item, index) => ({
      label: getEduLabel(item.education_label), // Education label
      data: years.map((year) => parseInt(item[year] || 0)), // Totals for each year
      backgroundColor: `rgba(${54 + index * 50}, ${162 - index * 30}, ${
        235 - index * 20
      }, 0.7)`,
      borderColor: `rgba(${54 + index * 50}, ${162 - index * 30}, ${
        235 - index * 20
      }, 1)`,
      borderWidth: 1,
    })),
  };

  // Chart options with animations and 3D-like effects
  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Education Totals by Year (2018-2024)",
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${context.parsed.y.toLocaleString("en-US")}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Year",
        },
        stacked: true, // Enable stacking on X-axis
      },
      y: {
        title: {
          display: true,
          text: "Total Count",
        },
        beginAtZero: true,
        stacked: true, // Enable stacking on Y-axis
        ticks: {
          callback: function (value) {
            return value.toLocaleString("en-US");
          },
        },
      },
    },
    animation: {
      duration: 1500,
      easing: "easeInOutBounce", // Smooth animations
    },
  };

  // PDF Generation function
  const downloadPDF = () => {
    const input = pdfRef.current;
    const originalWidth = input.style.width;
    input.style.width = "1100px"; // Temporarily increase width for better PDF capture

    html2canvas(input, { scale: 3 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Add header and footer
      // Header
      pdf.addImage(utahData, "PNG", 60, 10, 32, 16); // Logo
      pdf.setFontSize(26);
      pdf.text("UTahData.org", 95, 22);
      pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });

      pdf.setFontSize(12);
      pdf.setLineWidth(0.5);
      pdf.line(10, 35, 200, 35); // Line after header

      // Add main content
      const headerHeight = 38;
      pdf.addImage(imgData, "PNG", 5, headerHeight, imgWidth, imgHeight);

      // Footer
      pdf.line(10, 260, 200, 260); // Line before footer
      const footerStartY = 265;
      pdf.addImage(utahData, "PNG", 22, 265, 30, 15);
      pdf.setFontSize(10);
      pdf.setTextColor(0, 122, 255);
      pdf.text("Built by", 55, footerStartY + 10);
      pdf.addImage(bodex, "PNG", 68, footerStartY + 4, 44, 10);
      pdf.text("using", 112, footerStartY + 10);
      pdf.addImage(kpi, "PNG", 125, footerStartY, 20, 20);
      pdf.text(" 20250916.v3.198", 148, footerStartY + 10);

      // Date and URL
      const currentDate = new Date().toLocaleDateString();
      pdf.setFontSize(10);
      pdf.text(`Downloaded on: ${currentDate}`, 150, 295);
      const currentURL = window.location.href;
      pdf.text(`Downloaded from: ${currentURL}`, 10, 295);

      pdf.save("yearly-chart-data.pdf");
      input.style.width = originalWidth; // Reset width
    });
  };

  return (
    <>
      <div ref={pdfRef} className="content-to-export">
        <div style={{ padding: "20px" }}>
          <h2>Employed Males in All Education Categories Table</h2>
          {/* Table Section */}
          <div className="overflow-x-auto">
            <table className="table table-striped bg-white">
              <thead>
                <tr>
                  <th>Education Label</th>
                  <th>Total 2018</th>
                  <th>Total 2019</th>
                  <th>Total 2020</th>
                  <th>Total 2021</th>
                  <th>Total 2022</th>
                  <th>Total 2023</th>
                  <th>Total 2024</th>
                  <th>Average</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, index) => (
                  <tr key={index}>
                    <td>{getEduLabel(row.education_label)}</td>
                    <td>{Number(row.total_2018 || 0).toLocaleString("en-US")}</td>
                    <td>{Number(row.total_2019 || 0).toLocaleString("en-US")}</td>
                    <td>{Number(row.total_2020 || 0).toLocaleString("en-US")}</td>
                    <td>{Number(row.total_2021 || 0).toLocaleString("en-US")}</td>
                    <td>{Number(row.total_2022 || 0).toLocaleString("en-US")}</td>
                    <td>{Number(row.total_2023 || 0).toLocaleString("en-US")}</td>
                    <td>{Number(row.total_2024 || 0).toLocaleString("en-US")}</td>
                    <td>{Number(row.average || 0).toLocaleString("en-US")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div
          style={{
            width: "90%",
            maxWidth: "800px",
            margin: "auto",
            overflowX: "auto",
          }}
        >
          {" "}
          <div
            style={{
              width: "100%", // Takes 100% width of the parent div, but will scroll if needed
              minWidth: "600px", // Ensures a minimum width, enabling scroll when the chart exceeds screen size
              background: "#fff",
            }}
          >
            <div
              style={{ perspective: "1000px", transform: "rotateX(-10deg)" }}
            >
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>
      <br />
      {/* Download PDF Button */}
      <div className="flex justify-center">
        <button
          onClick={downloadPDF}
          className="download-btn bg-red-800 hover:bg-red-950 p-2 text-white rounded-lg flex items-center justify-between"
        >
          <span>Download as PDF </span>
          <span>
            <img className="w-[2rem]" src={pdf} onClick={downloadPDF} />
          </span>
        </button>
      </div>
    </>
  );
};

export default EducationTotalsStackedChart;
